import { useApp } from "providers";
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { TokenType } from "types/token.type";
import { fetchCoin, fetchCoins } from "utils/axios";
import { getTokenMarketSummary, normalizeToken } from "utils/helpers";

type TokenSummary = ReturnType<typeof getTokenMarketSummary>;

export const useTokens = (limit: number = 100) => {
  const { tokens, setTokens, pepeTokenPrice } = useApp(); // Global token state + live ETH price
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false); // Track if tokens have been fetched

  const fetchTokens = useCallback(async () => {
    if (hasFetched.current) return; // Skip if we've already fetched
    setLoading(true);
    setError(null); // Reset error state

    try {
      const data: TokenType[] = await fetchCoins();

      if (!data || data.length === 0) {
        // Explicitly handle the empty response case
        setError("No Token");
        setTokens([]);
        return;
      }

      const getCreatedTime = (token?: TokenType) => {
        const raw = token?.createdAt ?? "";
        const parsed = Date.parse(raw);
        if (!Number.isFinite(parsed)) {
          return 0;
        }
        return parsed;
      };

      // Sort newest launches first; fallback keeps ordering stable when timestamps missing
      const sortedData = [...data].sort((a, b) => getCreatedTime(b) - getCreatedTime(a));

      setTokens(sortedData);
      hasFetched.current = true;
    } catch (err) {
      console.error(err);
      setError("An error occurred while fetching tokens.");
    } finally {
      setLoading(false);
    }
  }, [setTokens]);

  const refetch = useCallback(async () => {
    hasFetched.current = false;
    await fetchTokens();
  }, [fetchTokens]);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens, limit]);

  const summaries: TokenSummary[] = useMemo(() => {
    if (!Array.isArray(tokens)) {
      return [];
    }
    const price = typeof pepeTokenPrice === "number" ? pepeTokenPrice : 0;
    return tokens.map((token) => getTokenMarketSummary(token, price));
  }, [tokens, pepeTokenPrice]);

  return {
    tokens: tokens || [],
    summaries,
    loading,
    error,
    refetch,
  };
};

export const useToken = (contractAddress: string) => {
  const { tokens: contextTokens = [], pepeTokenPrice } = useApp();
  const [rawToken, setRawToken] = useState<TokenType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchToken = useCallback(async (address: string) => {
    if (!address) {
      setRawToken(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await fetchCoin(address);
      if (data) {
        setRawToken(data);
      } else {
        setRawToken(null);
        setError("Token not found.");
      }
    } catch (err) {
      console.error(err);
      setError("An error occurred while fetching token details.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!contractAddress) {
      setRawToken(null);
      setLoading(false);
      return;
    }

    const existing = contextTokens.find(
      (token) => token.token?.toLowerCase() === contractAddress.toLowerCase()
    );

    if (existing) {
      setRawToken(existing);
      setLoading(false);
      setError(null);
      return;
    }

    fetchToken(contractAddress);
  }, [contractAddress, contextTokens, fetchToken]);

  const refetch = useCallback(async () => {
    await fetchToken(contractAddress);
  }, [contractAddress, fetchToken]);

  const normalizedToken = useMemo(() => {
    if (!rawToken) {
      return null;
    }
    const price =
      typeof pepeTokenPrice === "number" && Number.isFinite(pepeTokenPrice)
        ? pepeTokenPrice
        : rawToken.ethPriceUsd ?? 0;
    return normalizeToken(rawToken, price);
  }, [rawToken, pepeTokenPrice]);

  const summary = useMemo(() => {
    if (!normalizedToken) {
      return null;
    }
    const price =
      typeof pepeTokenPrice === "number" && Number.isFinite(pepeTokenPrice)
        ? pepeTokenPrice
        : normalizedToken.ethPriceUsd ?? 0;
    return getTokenMarketSummary(normalizedToken, price);
  }, [normalizedToken, pepeTokenPrice]);

  return {
    token: normalizedToken,
    summary,
    loading,
    error,
    refetch,
  };
};
